import type { CSSProperties } from "react";
import Button from "@acko-tech/building-blocks.ui.button-old";
import {
  $white,
  Box,
  Flex,
  styled
} from "@acko-tech/building-blocks.ui.common";
import { Text } from "@acko-tech/building-blocks.ui.typography";
import { desktopRightContainerWidth } from "@utils/index";

/** Premium savings strip slide-up duration (ms) — also used for transition timing */
export const PREMIUM_SAVINGS_BANNER_SLIDE_MS = 680;

export function getPremiumSavingsBannerSlotStyle(params: {
  isDesktop: boolean;
  slideRevealed: boolean;
}): CSSProperties {
  const { isDesktop, slideRevealed } = params;
  const translateY = slideRevealed ? 0 : "100%";
  const base: CSSProperties = {
    position: "absolute",
    bottom: "100%",
    left: "50%",
    right: "auto",
    transform: `translateX(-50%) translateY(${translateY})`,
    transition: `transform ${PREMIUM_SAVINGS_BANNER_SLIDE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
    zIndex: 1,
    boxSizing: "border-box"
  };

  if (isDesktop) {
    return {
      ...base,
      width: desktopRightContainerWidth,
      maxWidth: "100%",
      paddingLeft: 12,
      paddingRight: 12,
      marginBottom: 2
    };
  }

  return {
    ...base,
    width: "100vw",
    maxWidth: "100vw",
    paddingLeft: 0,
    paddingRight: 0,
    marginBottom: 0
  };
}

export const BUTTON_SUCCESS = "#0FA457";
export const BUTTON_DISABLED = "#C8D0DB";

export const Wrapper = styled(Flex)`
  width: ${desktopRightContainerWidth};
  box-sizing: border-box;
  flex-direction: column;

  @media (max-width: 767px) {
    width: max-content;
  }
  @media (max-width: 768px) {
    border-radius: unset;
    width: 100%;
  }
`;

export const FooterWrapper = styled(Box)`
  box-sizing: border-box;
  padding: 16px 20px calc(16px + env(safe-area-inset-bottom, 0px)) 20px;
  box-shadow: 0px -4px 8px -2px rgba(54, 53, 76, 0.06);
  background: #fff;
  border-radius: 16px 16px 0px 0px;
  position: relative;

  @media (max-width: 768px) {
    border-radius: unset;
    padding: 12px 20px calc(20px + env(safe-area-inset-bottom, 0px)) 20px;
  }
  ${(props) => props.styles}
  ${(props: { wrapperStyles: any }) => props.wrapperStyles}
  /* After injected styles: BFC so white bg height includes flex row (no float collapse) */
  display: flow-root;
`;

export const FooterContainer = styled(Flex)`
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  position: fixed;
  justify-content: center;
  z-index: 3;
`;

export const FooterHiddenBox = styled(Box)`
  width: 20%;
  @media (max-width: 768px) {
    display: none;
  }
`;

export const ConfirmButton = styled(Button)`
  padding: ${(props: any) => props.padding};
  background: ${(props: any) =>
    props.disable ? BUTTON_SUCCESS : BUTTON_DISABLED};
  @media (min-width: 780px) {
    width: 200px;
    margin-right: 20px;
  }
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: ${$white};
  border: none;
  outline: none;
  cursor: pointer;
  width: ${(props: any) => props.width};
  &:hover {
    border: 0px;
    color: ${$white};
    background: ${(props: any) =>
      props.disable ? BUTTON_SUCCESS : BUTTON_DISABLED};
  }
`;

export const CueText = styled(Text)`
  margin-top: 12px;
  color: #5b5675;
  font-family: 'Euclid Circular B', 'Inter', sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
`;

export const DisclaimerTip = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
  font-size: 16px;
  line-height: 12px;
  color: #040222;
  margin-bottom: -1rem;
  padding-bottom: 1rem;
  border-radius: 10px 10px 0px 0px;
  border-radius: 0px;

  @media (min-width: 768px) {
    display: flex;
    justify-content: center;
    border-radius: 10px 10px 0px 0px;
  }

  ${(props) => {
    switch (props.type) {
      case "INFO":
        return `
      background: linear-gradient(91deg, #fff4da 0%, #fff0e5 100%);
      `;
      case "SUCCESS":
        return `
        background: #EBFBEE;
      `;
      case "GENERAL":
        return `
        background: #EFEFF5;
      `;
      default:
        return `
        background: linear-gradient(91deg, #fff4da 0%, #fff0e5 100%);
        `;
    }
  }}
`;

export const DisclaimerWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;

  @media (min-width: 768px) {
    display: flex;
    width: ${desktopRightContainerWidth};
    justify-content: center;
  }
`;

export const DisclaimerContainer = styled.div`
  padding: 12px 20px;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
  width: max-content;
  gap: 0.5rem;
  width: 100%;
  box-sizing: border-box;

  @media (min-width: 768px) {
    justify-content: center;
  }

  ${(props) => props.styles}
`;

export const DisclaimerText = styled(Text)`
  color: #36354c;

  font-size: 12px;
  font-weight: 400;
  line-height: 18px;

  @media (min-width: 768px) {
    font-size: 14px;
    line-height: 20px;
  }
`;
